import { createSlice } from '@reduxjs/toolkit';
import type { BuilderState } from './types';

const initialState: BuilderState = {
  isLoading: false,
  error: false,
  pathway: null,
  topics: [],
  hasMore: true,
  index: 0,
  step: 10
};

export const slice = createSlice({
  name: 'builder',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    cleanPathway(state) {
      state.pathway = null;
      state.topics = [];
      state.index = 0;
      state.step = 10;
      state.hasMore = true;
      state.isLoading = false;
      state.error = false;
    },
    addTopic(state, action) {
      state.topics = [...state.topics, action.payload];
    },
    getPathwaySuccess(state, action) {
      state.isLoading = false;
      state.pathway = action.payload;
    },
    updateIsFullView(state, action) {
      state.isLoading = false;
      if (state.pathway) {
        state.pathway.isFullView = action.payload;
      }
    },
    topicSaveSuccess(state, action) {
      state.topics = [...state.topics, action.payload];
    },
    topicUpdateSuccess(state, action) {
      const topics = state.topics.map((item) =>
        item.guid === action.payload.guid
          ? {
              ...item,
              title: action.payload.title,
              description: action.payload.description,
              descriptionJson: action.payload.descriptionJson,
              color: action.payload.color,
              published: action.payload.published,
              url: action.payload.url,
              type: action.payload.type,
              resourceType: action.payload.resourceType,
              updatedAt: action.payload.updatedAt
            }
          : item
      );
      state.topics = topics;
    },
    topicCompletionUpdated(state, action) {
      const topic = state.topics.find((item) => item.guid === action.payload.guid);
      if (topic) topic.published = action.payload.published;
    },
    deleteTopicSuccess(state, action) {
      const idx = state.topics.map((item) => item.guid).indexOf(action.payload);
      state.topics = [...state.topics.slice(0, idx), ...state.topics.slice(idx + 1)];
    },

    resourceSaveSuccess(state, action) {
      const topicIndex = state.topics.findIndex((item) => item.guid === action.payload.topicGuid);

      if (topicIndex === -1) return;

      if (!state.topics[topicIndex].resources) state.topics[topicIndex].resources = [];

      state.topics[topicIndex].resources.push(action.payload);
    },
    resourceUpdateSuccess(state, action) {
      state.topics = state.topics.map((topic) => {
        if (topic.guid !== action.payload.topicGuid) return topic;

        return {
          ...topic,
          resources: topic.resources.map((resource) =>
            resource.guid === action.payload.guid
              ? {
                  ...resource,
                  title: action.payload.title,
                  content: action.payload.content,
                  contentJson: action.payload.contentJson,
                  url: action.payload.url,
                  published: action.payload.published,
                  updatedAt: action.payload.updatedAt
                }
              : resource
          )
        };
      });
    },
    resourceDeleteSuccess(state, action) {
      const topics = state.topics.map((topic) =>
        // https://stackoverflow.com/questions/38375646/filtering-array-of-objects-with-arrays-based-on-nested-value
        ({
          ...topic,
          resources: topic.resources.filter((item) => item.guid !== action.payload.resourceGuid)
        })
      );
      state.topics = topics;
    },
    getTopics(state, action) {
      state.isLoading = false;
      if (state.index === 0) {
        state.topics = action.payload;
      } else {
        state.topics = [...state.topics, ...action.payload];
      }
    },
    updateTopicOrder(state, action) {
      state.isLoading = false;
      state.topics = action.payload;
    },
    noHasMore(state) {
      state.hasMore = false;
    },
    resetTopics(state) {
      state.topics = [];
      state.index = 0;
      state.hasMore = true;
    },
    getMoreTopics(state) {
      const setIndex = state.index + 1;
      state.index = setIndex;
    }
  }
});

// Reducer
export default slice.reducer;
export const { getMoreTopics, cleanPathway, addTopic, resetTopics } = slice.actions;
